import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Skeleton,
  Stack,
  Badge,
  Alert,
} from "@mui/material";
import { toast } from "react-toastify";
import {
  Search,
  Clear,
  Visibility,
  Download,
  CheckCircle,
  Cancel,
  FileDownload,
  FilterList,
  Sort,
  Warning,
  Send,
  Edit,
  Tune,
  Close,
} from "@mui/icons-material";
import * as myApi from "../../components/Api";
import PaperDetail from "../../components/Paper/Detail";
import ConfirmationDialog from "../../components/UI/ConfirmationDialog";

/**
 * Modern Paper List Page with Material UI
 * Features:
 * - Advanced filtering (search, status, type)
 * - Sortable columns
 * - Bulk actions (approve/reject)
 * - Responsive table (card view on mobile)
 * - Pagination with jump
 * - Paper detail modal
 * - Download functionality
 */
export default function PaperList() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [papers, setPapers] = useState([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [searchType, setSearchType] = useState("search");
  const [filter, setFilter] = useState("");
  const [keyword, setKeyword] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [paperTypeFilter, setPaperTypeFilter] = useState("");
  const [subThemeFilter, setSubThemeFilter] = useState("");
  const [subThemes, setSubThemes] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const activeFilterCount = [statusFilter, paperTypeFilter, subThemeFilter, keyword].filter(Boolean).length;

  const defaultSubThemes = [
    { id: 1, name: "Peningkatan Daya Saing Industri, Hilirisasi, dan Investasi" },
    { id: 2, name: "Ketahanan Pangan Terintegrasi dan Stabilitas Harga" },
    { id: 3, name: "Perdagang, Konektivitas, dan Integrasi Ekonomi Regional" },
    { id: 4, name: "Pariwiata dan Sumber Ekonomi Baru" },
    { id: 5, name: "Pengembangan UMKM, Inklusi Keuangan, dan Inovasi Pembiayaan" },
    { id: 6, name: "Perbaikan Kualitas Pembelajaran dan Peningkatan Lulusan Pendidikan Tinggi" },
  ];

  useEffect(() => {
    const fetchSubThemes = async () => {
      try {
        const response = await myApi.get("/sub-themes/");
        console.log("[List] Sub-themes response:", response.data);
        if (response.data && (response.data.result || response.data.data)) {
          setSubThemes(response.data.result || response.data.data);
        } else {
          setSubThemes(defaultSubThemes);
        }
      } catch (error) {
        console.error("Error fetching sub-themes, using defaults:", error);
        setSubThemes(defaultSubThemes);
      }
    };
    fetchSubThemes();
  }, []);
  const [selectedPapers, setSelectedPapers] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [selectedPaper, setSelectedPaper] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState(null); // { approved: boolean }
  const [submitConfirmOpen, setSubmitConfirmOpen] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(null); // { paper_code, email }
  const [hasActiveSubmission, setHasActiveSubmission] = useState(false); // Track if user has active submission

  const userRole = myApi.getUser()?.role || "peserta";

  const checkUserSubmissionStatus = async () => {
    try {
      const response = await myApi.get("/user/submission-status");
      console.log("[List] submission status response:", response.data);
      
      if (response.data && response.data.status === "success") {
        const hasSubmitted = response.data.hasSubmittedPaper || false;
        console.log("[List] hasSubmittedPaper:", hasSubmitted);
        setHasActiveSubmission(hasSubmitted);
      }
    } catch (error) {
      console.error("Error checking user submission status:", error);
      setHasActiveSubmission(false);
    }
  };

  // Helper functions for submit status
  const isNotSubmitted = (status) => {
    return !status || status === '-' || status === '' || status === 'draft';
  };

  const isSubmitted = (status) => {
    return status === 'submit';
  };

  // Handle paper submission
  const handlePaperSubmit = (paperCode, email, sub_theme) => {
    setPendingSubmit({ paper_code: paperCode, email, sub_theme });
    setSubmitConfirmOpen(true);
  };

  const handlePaperSubmitConfirm = async () => {
    if (!pendingSubmit) return;

    try {
      // Check deadline first
      const deadlineResponse = await myApi.get('/deadline');
      if (deadlineResponse.data.result) {
        const deadlineInfo = deadlineResponse.data.result;
        const dateNow = new Date();
        const dateLimit = new Date(deadlineInfo.general_deadline);

        if (dateNow > dateLimit) {
          toast("Submission deadline has passed", { type: "error" });
          setSubmitConfirmOpen(false);
          setPendingSubmit(null);
          return;
        }
      }

      // Submit the paper
      const response = await myApi.post("/submitPaper", {
        data_papercode: pendingSubmit.paper_code,
        data_email: pendingSubmit.email,
        data_sub_theme: pendingSubmit.sub_theme,
      });
      
      if (response.data && response.data.status === "success") {
        toast("Paper submitted successfully!", { type: "success" });
      } else {
        toast(response.data?.message || "Failed to submit paper", { type: "error" });
        return;
      }
      setSubmitConfirmOpen(false);
      setPendingSubmit(null);
      checkUserSubmissionStatus();
      fetchData();
    } catch (error) {
      toast(error.response?.data?.message || error.message || "Error submitting paper", { type: "error" });
    }
  };

  const fetchData = useCallback((pageNum = 1, count = 10) => {
    setLoading(true);

    const option = {
      search_type: searchType,
      user_role: userRole,
      filter: filter,
      keyword: keyword,
      page: pageNum,
      count: count,
      status_filter: statusFilter,
      paper_type_filter: paperTypeFilter,
      sub_theme: subThemeFilter,
    };

    setTimeout(() => {
      myApi
        .post("/paper/search/", option)
        .then((res) => {
          const result = res.data.data;
          setPapers(result.data || []);
          setPage(result.paginate?.page || 1);
          setRowsPerPage(result.count || 10);
          setTotalItems(result.total || 0);
          setTotalPages(result.paginate?.last || 1);

          // Use server-side stats (accurate across all pages)
          setStats({
            pending: result.stats?.pending ?? 0,
            approved: result.stats?.approved ?? 0,
            rejected: result.stats?.rejected ?? 0,
          });

          setLoading(false);
        })
        .catch((err) => {
          toast(err.message || "Error fetching data", { type: "error" });
          setLoading(false);
        });
    }, 500);
  }, [searchType, filter, keyword, statusFilter, paperTypeFilter, subThemeFilter, userRole]);

  useEffect(() => {
    if (userRole !== "admin") {
      checkUserSubmissionStatus();
    }
    fetchData();
  // Re-fetch whenever the user navigates to this page (location.key changes on each navigation)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const handleSearch = (e) => {
    const filterValue = e.target.name === "filterBy" ? e.target.value : filter;
    const keywordValue = e.target.name === "keyword" ? e.target.value : keyword;

    setFilter(filterValue);
    setKeyword(keywordValue);
  };

  const handleApplySearch = () => {
    setPage(1);
    fetchData(1);
  };

const handleClearSearch = () => {
    setFilter("");
    setKeyword("");
    setStatusFilter("");
    setPaperTypeFilter("");
    setSubThemeFilter("");
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage + 1);
    fetchData(newPage + 1, rowsPerPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
    fetchData(1, parseInt(event.target.value, 10));
  };

  const toggleSelectPaper = (paperCode) => {
    if (selectedPapers.includes(paperCode)) {
      setSelectedPapers(selectedPapers.filter((code) => code !== paperCode));
    } else {
      setSelectedPapers([...selectedPapers, paperCode]);
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedPapers(papers.map((p) => p.paper_code));
    } else {
      setSelectedPapers([]);
    }
  };

  const handleBulkApproval = (isApproved) => {
    if (selectedPapers.length === 0) {
      toast("Please select at least one paper", { type: "warning" });
      return;
    }

    // Open confirmation dialog instead of window.confirm
    setBulkAction({ approved: isApproved });
    setBulkConfirmOpen(true);
  };

  const handleBulkConfirm = () => {
    if (!bulkAction) return;

    const { approved } = bulkAction;
    const approvalData = {
      paper_codes: selectedPapers,
      paper_status: approved ? "lolos" : "gagal",
    };

    myApi.post("/paper/bulk-approval/", approvalData).then((res) => {
      if (res.data?.status === "success") {
        toast(`${selectedPapers.length} paper(s) successfully ${approved ? "approved" : "rejected"}!`, { type: "success" });
      } else {
        toast(res.data?.message || `Failed to ${approved ? "approve" : "reject"} papers`, { type: "error" });
        return;
      }
      setSelectedPapers([]);
      setBulkAction(null);
      fetchData();
    }).catch((err) => {
      toast(err.response?.data?.message || err.message || "Error processing bulk action", { type: "error" });
    });
  };

  const handleApproval = (paperCode, isApproved = true) => {
    // Open confirmation dialog instead of window.confirm
    setBulkAction({ approved: isApproved, single: true, paperCode });
    setBulkConfirmOpen(true);
  };

  const handleSingleConfirm = () => {
    if (!bulkAction) return;

    const { approved, paperCode } = bulkAction;

    myApi.post("/paper/approval/", { paper_code: paperCode, paper_status: approved ? "lolos" : "gagal" })
      .then((res) => {
        if (res.data?.status === "success") {
          toast(`Paper ${approved ? "approved" : "rejected"} successfully!`, { type: "success" });
        } else {
          toast(res.data?.message || `Failed to ${approved ? "approve" : "reject"} paper`, { type: "error" });
          return;
        }
        setBulkAction(null);
        fetchData();
      })
      .catch((err) => {
        toast(err.response?.data?.message || err.message || "Error updating paper", { type: "error" });
      });
  };

  const handleViewDetails = (paper) => {
    setSelectedPaper(paper);
    setDetailOpen(true);
  };

  const handleDownload = (paper) => {
    if (paper.paper_filePath_1) {
      const baseUrl = window.env?.API_URL || process.env.REACT_APP_API_URL || "http://localhost:3001";
      const link = document.createElement("a");
      link.href = `${baseUrl}/${paper.paper_filePath_1}`;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.download = paper.paper_fileName_1 || "";
      link.click();
      toast("Downloading paper...", { type: "info" });
    } else {
      toast("Paper file not available", { type: "warning" });
    }
  };

  const getStatusChip = (status, submitStatus) => {
    // Not submitted papers
    if (!submitStatus || submitStatus === 'draft' || submitStatus === '-' || submitStatus === '') {
      return <Chip label="Not Submitted" color="default" size="small" />;
    }

    // Submitted papers
    const statusMap = {
      "-": { label: "Waiting for Approval", color: "warning" },
      "pending": { label: "Waiting for Approval", color: "warning" },
      lolos: { label: "Approved", color: "success" },
      gagal: { label: "Rejected", color: "error" },
    };
    const config = statusMap[status] || { label: status, color: "default" };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const getTypeChip = (category) => {
    const isPM = category === "Mahasiswa";
    return (
      <Chip
        label={isPM ? "PM" : "PU"}
        size="small"
        color={isPM ? "primary" : "secondary"}
        sx={{ fontWeight: 600 }}
      />
    );
  };

  if (loading && papers.length === 0) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Skeleton variant="rectangular" height={60} sx={{ mb: 3, borderRadius: 2 }} />
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Paper Submissions
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage and review all submitted papers
          </Typography>
        </Box>
        {userRole !== "admin" && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate("/paper/add/")}
            startIcon={<FileDownload />}
          >
            Create New Paper
          </Button>
        )}
      </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <Paper sx={{ p: 3, borderRadius: 3, bgcolor: "warning.light", color: "warning.contrastText" }}>
            <Typography variant="h4" fontWeight={600}>{stats.pending}</Typography>
            <Typography variant="body2">Waiting for Approval</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Paper sx={{ p: 3, borderRadius: 3, bgcolor: "success.light", color: "success.contrastText" }}>
            <Typography variant="h4" fontWeight={600}>{stats.approved}</Typography>
            <Typography variant="body2">Approved</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Paper sx={{ p: 3, borderRadius: 3, bgcolor: "error.light", color: "error.contrastText" }}>
            <Typography variant="h4" fontWeight={600}>{stats.rejected}</Typography>
            <Typography variant="body2">Rejected</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Filters Toggle */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant={showFilters ? "outlined" : "tonal"}
          onClick={() => setShowFilters(!showFilters)}
          startIcon={<Tune />}
          size="small"
          color={showFilters ? "primary" : "inherit"}
        >
          {showFilters ? "Hide Filters" : "Show Filters"} ({activeFilterCount})
        </Button>
      </Box>

      {/* Filters Panel */}
      {showFilters && (
        <Paper sx={{ p: 3, mb: 3, borderRadius: 3, bgcolor: 'grey.50' }}>
          <Grid container spacing={2} alignItems="center">
            {/* Search By */}
            <Grid item xs={6} sm={4} md={2} sx={{ minWidth: 140, maxWidth: 160 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Search By</InputLabel>
                <Select
                  name="filterBy"
                  value={filter}
                  label="Search By"
                  onChange={handleSearch}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="kodepaper">Code</MenuItem>
                  <MenuItem value="nama">Author</MenuItem>
                  <MenuItem value="title">Title</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Keyword */}
            <Grid item xs={6} sm={4} md={3} sx={{ minWidth: 180, maxWidth: 280 }}>
              <TextField
                fullWidth
                size="small"
                id="keyword"
                name="keyword"
                placeholder={filter ? `${filter}...` : "Value..."}
                value={keyword}
                onChange={handleSearch}
                onKeyPress={(e) => e.key === "Enter" && handleApplySearch()}
              />
            </Grid>

            {/* Status */}
            <Grid item xs={6} sm={4} md={2} sx={{ minWidth: 120, maxWidth: 150 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="pending">Waiting</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Type */}
            <Grid item xs={6} sm={4} md={2} sx={{ minWidth: 100, maxWidth: 130 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select
                  value={paperTypeFilter}
                  label="Type"
                  onChange={(e) => setPaperTypeFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="PM">Mahasiswa</MenuItem>
                  <MenuItem value="PU">Umum</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Sub-Theme */}
            <Grid item xs={12} sm={6} md={2} sx={{ minWidth: 180, maxWidth: 320 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Sub-Theme</InputLabel>
                <Select
                  value={subThemeFilter}
                  label="Sub-Theme"
                  onChange={(e) => setSubThemeFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {subThemes.map((theme) => (
                    <MenuItem key={theme.id} value={theme.name}>{theme.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12} sm={6} md={1} sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button 
                variant="contained" 
                onClick={handleApplySearch} 
                startIcon={<Search />}
              >
                Search
              </Button>
              {activeFilterCount > 0 && (
                <IconButton 
                  onClick={handleClearSearch} 
                  color="error"
                  title="Clear All Filters"
                >
                  <Close />
                </IconButton>
              )}
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Bulk Actions */}
      {selectedPapers.length > 0 && userRole === "admin" && (
        <Paper sx={{ p: 2, mb: 3, borderRadius: 3, bgcolor: "primary.light" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="body1" fontWeight={600}>
              {selectedPapers.length} paper(s) selected
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                color="success"
                size="small"
                startIcon={<CheckCircle />}
                onClick={() => handleBulkApproval(true)}
              >
                Approve Selected
              </Button>
              <Button
                variant="contained"
                color="error"
                size="small"
                startIcon={<Cancel />}
                onClick={() => handleBulkApproval(false)}
              >
                Reject Selected
              </Button>
            </Stack>
          </Box>
        </Paper>
      )}

      {/* Table */}
      <Paper sx={{ borderRadius: 3, overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "grey.100" }}>
                <TableCell padding="checkbox" align="center">
                  {userRole === "admin" && (
                    <Checkbox
                      checked={selectedPapers.length === papers.length && papers.length > 0}
                      onChange={handleSelectAll}
                    />
                  )}
                </TableCell>
                <TableCell align="center"><strong>Paper Code</strong></TableCell>
                <TableCell align="center"><strong>Title</strong></TableCell>
                <TableCell align="center"><strong>Author</strong></TableCell>
                <TableCell align="center"><strong>Type</strong></TableCell>
                <TableCell align="center"><strong>Status</strong></TableCell>
                <TableCell align="center"><strong>Submission Date</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {papers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Alert severity="info" sx={{ my: 2 }}>
                      No papers found. Adjust your filters or submit a new paper.
                    </Alert>
                  </TableCell>
                </TableRow>
              ) : (
                papers.map((paper) => (
                  <TableRow key={paper.paper_code} hover selected={selectedPapers.includes(paper.paper_code)}>
                    <TableCell padding="checkbox">
                      {userRole === "admin" && (
                        <Checkbox
                          checked={selectedPapers.includes(paper.paper_code)}
                          onChange={() => toggleSelectPaper(paper.paper_code)}
                        />
                      )}
                    </TableCell>
                    <TableCell>{paper.paper_code}</TableCell>
                    <TableCell sx={{ maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis" }}>
                      {paper.title}
                    </TableCell>
                    <TableCell>{paper.name_1}</TableCell>
                    <TableCell>{getTypeChip(paper.category)}</TableCell>
                    <TableCell>{getStatusChip(paper.paper_status, paper.submit_status)}</TableCell>
                    <TableCell>
                      {isSubmitted(paper.submit_status) && paper.submission_date && paper.submission_date !== "-"
                        ? new Date(paper.submission_date).toLocaleDateString()
                        : "-"}
                    </TableCell>
                    <TableCell align="left">
                      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                        {/* Edit Button - Only for draft papers */}
                        {userRole !== "admin" && isNotSubmitted(paper.submit_status) && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            startIcon={<Edit sx={{ fontSize: 16 }} />}
                            onClick={() => navigate(`/paper/edit/${paper.paper_code}/`)}
                            sx={{ 
                              px: 1, 
                              py: 0.3, 
                              fontSize: '0.7rem',
                              minWidth: 'auto',
                              '& .MuiButton-startIcon': { mr: 0.5 }
                            }}
                          >
                            Edit
                          </Button>
                        )}

                        {/* View Details Button */}
                        <Button
                          size="small"
                          variant="outlined"
                          color="primary"
                          startIcon={<Visibility sx={{ fontSize: 16 }} />}
                          onClick={() => handleViewDetails(paper)}
                          sx={{ 
                            px: 1, 
                            py: 0.3, 
                            fontSize: '0.7rem',
                            minWidth: 'auto',
                            '& .MuiButton-startIcon': { mr: 0.5 }
                          }}
                        >
                          View
                        </Button>

                        {/* Download Button */}
                        <Button
                          size="small"
                          variant="outlined"
                          color="inherit"
                          startIcon={<Download sx={{ fontSize: 16 }} />}
                          onClick={() => handleDownload(paper)}
                          sx={{ 
                            px: 1, 
                            py: 0.3, 
                            fontSize: '0.7rem',
                            minWidth: 'auto',
                            '& .MuiButton-startIcon': { mr: 0.5 }
                          }}
                        >
                          Download
                        </Button>

                        {/* User Submit Button - Only show for non-admin and unsubmitted papers */}
                        {userRole !== "admin" && isNotSubmitted(paper.submit_status) && (
                          hasActiveSubmission ? (
                            <Tooltip title="You can only submit one paper.">
                              <span>
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="disabled"
                                  startIcon={<Send sx={{ fontSize: 16 }} />}
                                  disabled
                                  sx={{
                                    px: 1,
                                    py: 0.3,
                                    fontSize: '0.7rem',
                                    minWidth: 'auto',
                                    opacity: 0.6,
                                    '& .MuiButton-startIcon': { mr: 0.5 }
                                  }}
                                >
                                  Submit
                                </Button>
                              </span>
                            </Tooltip>
                          ) : (
                            <Button
                              size="small"
                              variant="contained"
                              color="info"
                              startIcon={<Send sx={{ fontSize: 16 }} />}
                              onClick={() => handlePaperSubmit(paper.paper_code, paper.email_1, paper.sub_theme)}
                              sx={{
                                px: 1,
                                py: 0.3,
                                fontSize: '0.7rem',
                                minWidth: 'auto',
                                '& .MuiButton-startIcon': { mr: 0.5 }
                              }}
                            >
                              Submit
                            </Button>
                          )
                        )}

                        {/* Show Submitted badge for submitted papers */}
                        {userRole !== "admin" && isSubmitted(paper.submit_status) && (
                          <Chip
                            label="Submitted"
                            size="small"
                            color="success"
                            icon={<CheckCircle sx={{ fontSize: 14 }} />}
                            sx={{ height: 22, fontSize: '0.65rem' }}
                          />
                        )}

                        {/* Admin Actions - Only for submitted papers */}
                        {userRole === "admin" && paper.paper_status === "-" && isSubmitted(paper.submit_status) && (
                          <>
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              startIcon={<CheckCircle sx={{ fontSize: 16 }} />}
                              onClick={() => handleApproval(paper.paper_code, true)}
                              sx={{ 
                                px: 1, 
                                py: 0.3, 
                                fontSize: '0.7rem',
                                minWidth: 'auto',
                                '& .MuiButton-startIcon': { mr: 0.5 }
                              }}
                            >
                              Approve
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              color="error"
                              startIcon={<Cancel sx={{ fontSize: 16 }} />}
                              onClick={() => handleApproval(paper.paper_code, false)}
                              sx={{ 
                                px: 1, 
                                py: 0.3, 
                                fontSize: '0.7rem',
                                minWidth: 'auto',
                                '& .MuiButton-startIcon': { mr: 0.5 }
                              }}
                            >
                              Reject
                            </Button>
                          </>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={totalItems}
          page={page - 1}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
        />
      </Paper>

      {/* Paper Detail Modal */}
      {selectedPaper && (
        <Dialog
          open={detailOpen}
          onClose={() => setDetailOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Paper Details - {selectedPaper.paper_code}
          </DialogTitle>
          <DialogContent dividers>
            <PaperDetail 
              paper={selectedPaper} 
              onClose={() => setDetailOpen(false)} 
              onUpdate={(updatedPaper) => {
                setPapers(papers.map(p => p.paper_code === updatedPaper.paper_code ? updatedPaper : p));
                setSelectedPaper(updatedPaper);
              }} 
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Bulk/Single Approval Confirmation Dialog */}
      <ConfirmationDialog
        open={bulkConfirmOpen}
        onClose={() => {
          setBulkConfirmOpen(false);
          setBulkAction(null);
        }}
        onConfirm={bulkAction?.single ? handleSingleConfirm : handleBulkConfirm}
        title={bulkAction?.single ? "Confirm Action" : "Confirm Bulk Action"}
        message={
          bulkAction?.single
            ? `Are you sure you want to ${bulkAction.approved ? "approve" : "reject"} this paper?`
            : `Are you sure you want to ${bulkAction?.approved ? "approve" : "reject"} ${selectedPapers.length} selected paper(s)?`
        }
        confirmText={bulkAction?.approved ? "Approve" : "Reject"}
        cancelText="Cancel"
        variant={bulkAction?.approved ? "success" : "danger"}
      />

      {/* Paper Submit Confirmation Dialog */}
      <ConfirmationDialog
        open={submitConfirmOpen}
        onClose={() => {
          setSubmitConfirmOpen(false);
          setPendingSubmit(null);
        }}
        onConfirm={handlePaperSubmitConfirm}
        title="Submit Paper for Review"
        message="Are you sure you want to submit this paper for admin review? This action cannot be undone after the submission deadline."
        confirmText="Submit"
        cancelText="Cancel"
        variant="warning"
      />
    </Container>
  );
}
